import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Share2, X } from "lucide-react";
import { buildShareUrl } from "../lib/shareCart";

// Lightweight share-cart modal (Option A — URL-encoded, no backend).
// Renders a share link the user can copy, plus native Web Share on mobile
// when available. Closes on Escape and on backdrop click.
export const ShareCartModal = ({ open, onClose, cartState, itemCount }) => {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const inputRef = useRef(null);

  const shareUrl = useMemo(
    () => (open ? buildShareUrl(cartState) : ""),
    [open, cartState]
  );

  // Show a truncated preview so long URLs don't overflow the mobile modal.
  const displayUrl = useMemo(() => {
    if (!shareUrl) return "";
    if (shareUrl.length <= 90) return shareUrl;
    return `${shareUrl.slice(0, 60)}…${shareUrl.slice(-20)}`;
  }, [shareUrl]);

  useEffect(() => {
    if (!open) return undefined;
    setCopied(false);
    setShared(false);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Auto-select the link so users on desktop can hit Cmd/Ctrl+C immediately.
    const t = setTimeout(() => inputRef.current?.select(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // Fallback: select the input so the user can Cmd/Ctrl+C manually.
      inputRef.current?.select();
      if (typeof console !== "undefined") console.debug("share: clipboard write failed", err);
    }
  };

  const onNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "My Fresh Fast Foods cart",
        text: `Here's my catering cart — ${itemCount} item${itemCount === 1 ? "" : "s"}. Take a look:`,
        url: shareUrl,
      });
      setShared(true);
      setTimeout(() => setShared(false), 2200);
    } catch (err) {
      // User cancelled the sheet — nothing to do.
      if (typeof console !== "undefined") console.debug("share: native share cancelled", err);
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div
      data-testid="share-cart-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        // Backdrop click closes; clicks inside the panel don't bubble.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-cart-title"
        className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl border border-black/5 p-6 sm:p-7"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="share-cart-close"
          className="absolute top-4 right-4 p-1.5 rounded-full text-brand-charcoal/50 hover:text-brand-charcoal hover:bg-brand-cream/60 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-3 mb-1.5">
          <div className="h-10 w-10 rounded-2xl bg-brand-teal/10 flex items-center justify-center">
            <Share2 className="h-5 w-5 text-brand-teal" />
          </div>
          <h3
            id="share-cart-title"
            className="font-display font-black text-[20px] text-brand-charcoal tracking-tight"
          >
            Share this cart
          </h3>
        </div>
        <p className="text-[13.5px] text-brand-charcoal/65 mb-5 leading-relaxed">
          Send this link — the recipient opens it and lands on the same cart with{" "}
          <strong>{itemCount}</strong> item{itemCount === 1 ? "" : "s"} ready to review or checkout.
          Prices refresh live from the current menu.
        </p>

        <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-1.5">
          Shareable link
        </label>
        <div className="flex items-stretch gap-2">
          <input
            ref={inputRef}
            data-testid="share-cart-url"
            readOnly
            value={shareUrl}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 h-11 rounded-xl border border-black/10 bg-brand-cream/40 px-3 text-[13px] text-brand-charcoal font-mono truncate"
            title={shareUrl}
            aria-label="Shareable cart URL"
          />
          <button
            type="button"
            data-testid="share-cart-copy-btn"
            onClick={onCopy}
            className={`inline-flex items-center gap-1.5 px-3 h-11 rounded-xl font-bold text-[12.5px] uppercase tracking-[0.1em] transition-colors ${
              copied
                ? "bg-emerald-600 text-white"
                : "bg-brand-charcoal text-white hover:bg-brand-charcoal/90"
            }`}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="mt-2 text-[11.5px] text-brand-charcoal/45 break-all sm:break-normal">
          {displayUrl}
        </p>

        {canNativeShare && (
          <button
            type="button"
            data-testid="share-cart-native-btn"
            onClick={onNativeShare}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-brand-teal text-white font-bold text-[13px] uppercase tracking-[0.1em] hover:bg-brand-teal/90 transition-colors"
          >
            <Share2 className="h-4 w-4" />
            {shared ? "Shared" : "Share via…"}
          </button>
        )}

        <p className="mt-5 text-[11.5px] text-brand-charcoal/50 leading-relaxed">
          Anyone with this link can view and check out this cart. It doesn&apos;t share your name,
          address, or payment info.
        </p>
      </div>
    </div>
  );
};
